import { useState } from "react";
import axios from "axios";
import { Instagram, Mail, MapPin, Phone, Check } from "lucide-react";
import { useLocale } from "../i18n/LocaleContext";
import { TREATMENTS } from "../data/treatments";
import { Reveal, Eyebrow } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t, lang } = useLocale();
  const [form, setForm] = useState({ name: "", email: "", phone: "", treatment: "", message: "", consent: false, company: "" });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!form.name.trim() || !form.message.trim()) { setErrorMsg(t("form.required")); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setErrorMsg(t("form.invalidEmail")); return; }
    if (!form.consent) { setErrorMsg(t("form.privacyRequired")); return; }
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  const inputCls = "w-full border-b border-imperium-champagne/60 bg-transparent py-3.5 text-base font-light text-imperium-black placeholder:text-imperium-black/35 focus:border-imperium-gold focus:outline-none transition-colors";

  return (
    <div data-testid="contact-page">
      <section className="mx-auto max-w-[1600px] px-6 pt-40 sm:px-10 lg:px-16 lg:pt-52">
        <Reveal>
          <Eyebrow>{t("contact.eyebrow")}</Eyebrow>
          <h1 className="mt-6 max-w-3xl font-serif text-5xl font-light leading-tight text-imperium-black sm:text-6xl">{t("contact.title")}</h1>
          <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-imperium-black/70">{t("contact.intro")}</p>
        </Reveal>
      </section>

      <section className="mx-auto grid max-w-[1600px] gap-20 px-6 py-20 sm:px-10 lg:grid-cols-12 lg:px-16 lg:py-28">
        <div className="lg:col-span-5">
          <Reveal>
            <ul className="space-y-10">
              <li>
                <p className="text-[11px] font-label uppercase tracking-[0.3em] text-imperium-bronze">{t("contact.locationLabel")}</p>
                <p className="mt-3 flex items-center gap-3 font-serif text-2xl font-light text-imperium-black" data-testid="contact-location">
                  <MapPin className="h-5 w-5 text-imperium-gold" /> Austrasse 31, 4051 Basel
                </p>
                <p className="ml-8 mt-1 text-sm font-light text-imperium-black/60">Switzerland</p>
              </li>
              <li>
                <p className="text-[11px] font-label uppercase tracking-[0.3em] text-imperium-bronze">{t("contact.phoneLabel")}</p>
                <a href="tel:+41786136096" data-testid="contact-phone" className="link-line mt-3 inline-flex items-center gap-3 font-serif text-2xl font-light text-imperium-black">
                  <Phone className="h-5 w-5 text-imperium-gold" /> +41 78 613 60 96
                </a>
              </li>
              <li>
                <p className="text-[11px] font-label uppercase tracking-[0.3em] text-imperium-bronze">{t("contact.emailLabel")}</p>
                <div className="mt-3 flex flex-col gap-2">
                  <a href="mailto:kontakt@imperium-wellness.ch" data-testid="contact-email-main" className="link-line inline-flex items-center gap-3 text-base font-light text-imperium-black">
                    <Mail className="h-4 w-4 text-imperium-gold" /> kontakt@imperium-wellness.ch
                  </a>
                </div>
              </li>
              <li>
                <p className="text-[11px] font-label uppercase tracking-[0.3em] text-imperium-bronze">{t("contact.instagramLabel")}</p>
                <a href="https://www.instagram.com/imperiumbasel" target="_blank" rel="noopener noreferrer" data-testid="contact-instagram" className="link-line mt-3 inline-flex items-center gap-3 text-base font-light text-imperium-black">
                  <Instagram className="h-4 w-4 text-imperium-gold" /> @Imperiumbasel
                </a>
              </li>
            </ul>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.1}>
            {status === "success" ? (
              <div className="flex flex-col items-start gap-6 border border-imperium-champagne/50 bg-imperium-offwhite p-10" data-testid="form-success-message">
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-imperium-gold text-imperium-gold">
                  <Check className="h-5 w-5" />
                </span>
                <p className="font-serif text-2xl font-light leading-relaxed text-imperium-black">{t("form.success")}</p>
              </div>
            ) : (
              <form onSubmit={submit} noValidate data-testid="contact-form" className="border border-imperium-champagne/50 bg-imperium-offwhite p-8 sm:p-12">
                <h2 className="font-serif text-3xl font-light text-imperium-black">{t("contact.formTitle")}</h2>
                <div className="mt-10 grid gap-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="cf-name" className="text-[11px] font-label uppercase tracking-[0.25em] text-imperium-bronze">{t("form.name")} *</label>
                    <input id="cf-name" data-testid="contact-name-input" type="text" value={form.name} onChange={set("name")} className={inputCls} autoComplete="name" />
                  </div>
                  <div>
                    <label htmlFor="cf-email" className="text-[11px] font-label uppercase tracking-[0.25em] text-imperium-bronze">{t("form.email")} *</label>
                    <input id="cf-email" data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} className={inputCls} autoComplete="email" />
                  </div>
                  <div>
                    <label htmlFor="cf-phone" className="text-[11px] font-label uppercase tracking-[0.25em] text-imperium-bronze">{t("form.phone")}</label>
                    <input id="cf-phone" data-testid="contact-phone-input" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} autoComplete="tel" />
                  </div>
                  <div>
                    <label htmlFor="cf-treatment" className="text-[11px] font-label uppercase tracking-[0.25em] text-imperium-bronze">{t("form.treatment")}</label>
                    <select id="cf-treatment" data-testid="contact-treatment-select" value={form.treatment} onChange={set("treatment")} className={`${inputCls} cursor-pointer`}>
                      <option value="">{t("form.treatmentPlaceholder")}</option>
                      {TREATMENTS.map((tr) => (
                        <option key={tr.key} value={tr.names[lang]}>{tr.names[lang]}</option>
                      ))}
                      <option value="beratung">{t("form.noPreference")}</option>
                    </select>
                  </div>
                </div>
                <div className="mt-8">
                  <label htmlFor="cf-message" className="text-[11px] font-label uppercase tracking-[0.25em] text-imperium-bronze">{t("form.message")} *</label>
                  <textarea id="cf-message" data-testid="contact-message-input" rows={4} value={form.message} onChange={set("message")} placeholder={t("form.messagePlaceholder")} className={`${inputCls} resize-none`} />
                </div>
                <input type="text" name="company" value={form.company} onChange={set("company")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="pointer-events-none absolute left-[-9999px] h-0 w-0 opacity-0" />
                <div className="mt-8 flex items-start gap-4">
                  <input id="cf-consent" data-testid="contact-privacy-checkbox" type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-imperium-bronze" />
                  <label htmlFor="cf-consent" className="text-xs font-light leading-relaxed text-imperium-black/65">{t("form.privacy")}</label>
                </div>
                {errorMsg && <p className="mt-6 text-sm text-red-800" role="alert" data-testid="form-validation-error">{errorMsg}</p>}
                {status === "error" && <p className="mt-6 text-sm text-red-800" role="alert" data-testid="form-error-message">{t("form.error")}</p>}
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={status === "sending"}
                  className="mt-10 w-full border border-imperium-bronze px-8 py-4 text-[11px] font-label uppercase tracking-[0.25em] text-imperium-brown transition-colors duration-300 hover:bg-imperium-brown hover:text-imperium-white disabled:cursor-wait disabled:opacity-60 sm:w-auto"
                >
                  {status === "sending" ? t("form.sending") : t("form.submit")}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
